import type { FurnitureDef } from "@clubap/shared";
import { useEffect, useState } from "react";
import type { IglooSnapshot } from "../economy/igloo.js";

interface Props {
  snapshot: IglooSnapshot;
  /**
   * Comprar. Quién cobra y a quién se le apunta lo decide el adaptador de `App`: el
   * mismo panel compra para tu iglú o para la sala de un club, y acá no se sabe cuál.
   */
  onBuy: (furnitureId: string) => Promise<void>;
  /** El dinero es de un club: se dice, porque no es el tuyo. */
  caja?: boolean;
  onClose: () => void;
}

/**
 * La tienda de muebles: comprar, y nada más.
 *
 * Poner y quitar viven en la barra de decorar, no acá, por lo mismo que la tienda de
 * ropa no equipa: un panel que compra Y coloca tiene un botón que cambia de significado
 * según de dónde vengas. Acá sales con una pieza más en la barra; qué hacer con ella es
 * otra decisión, en otro sitio.
 */
export function IglooPanel({ snapshot, onBuy, caja = false, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const balance = snapshot.balance;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const buy = async (furniture: FurnitureDef) => {
    setBusy(furniture.id);
    setError(null);

    try {
      await onBuy(furniture.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo comprar.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="overlay">
      <div className="panel sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Muebles</h2>
          <span className="sheet__coins">
            {caja ? "CAJA " : ""}◉ {balance}
          </span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <ul className="sheet__list">
          {snapshot.furniture.map((furniture) => {
            const tengo = snapshot.owned[furniture.id] ?? 0;
            const puede = balance >= furniture.price;

            return (
              <li className="sheet__item" key={furniture.id}>
                <span className="sheet__name">
                  {furniture.name}
                  {tengo > 0 ? ` · tienes ${tengo}` : ""}
                </span>
                <span className={`sheet__price${puede ? "" : " sheet__price--short"}`}>
                  ◉ {furniture.price}
                </span>

                <button
                  className="btn btn--small"
                  type="button"
                  disabled={busy === furniture.id || !puede}
                  title={puede ? "Comprar" : `Faltan ${furniture.price - balance}`}
                  onClick={() => void buy(furniture)}
                >
                  {busy === furniture.id ? "…" : "Comprar"}
                </button>
              </li>
            );
          })}
        </ul>

        <p className="sheet__hint">
          {caja
            ? "Se paga de la caja del club, no de tu bolsillo. Lo que compres es del club."
            : "Lo que compres aparece en la barra de arriba. Arrástralo al piso para ponerlo."}
        </p>
      </div>
    </div>
  );
}
